import React, { useContext, useEffect, useState, useRef } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, IconButton } from '@material-ui/core';
import { DriverAuthContext, navigate } from '../DriverApp';

const OUTCOMES = [
  { key: 'resolved',        label: '✅ Resolvido',           color: '#16a34a' },
  { key: 'partial',         label: '🟠 Resolvido parcialmente', color: '#f59e0b' },
  { key: 'followup_needed', label: '🔄 Necessita nova visita',   color: '#3b82f6' },
  { key: 'customer_absent', label: '🚪 Cliente ausente',         color: '#64748b' },
  { key: 'cancelled',       label: '✖ Cancelada',               color: '#dc2626' },
];

// Lightweight signature pad — records strokes as a data URL.
function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
    }
  }, []); // eslint-disable-line

  function pos(e) {
    const c = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - c.left, y: t.clientY - c.top };
  }
  function start(e) {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  }
  function move(e) {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.stroke();
    last.current = p;
  }
  function end() {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  }
  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onChange('');
  }

  return (
    <Box>
      <canvas
        ref={canvasRef}
        width={340} height={140}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{
          border: '1px dashed #cbd5e1', borderRadius: 8, background: '#fff',
          width: '100%', maxWidth: 340, touchAction: 'none', display: 'block',
        }}
      />
      <Button size="small" onClick={clear} style={{ textTransform: 'none', fontSize: '0.7rem', color: '#64748b', marginTop: 4 }}>
        Limpar assinatura
      </Button>
    </Box>
  );
}

export default function DriverOrderDetail({ orderId }) {
  const { token, api } = useContext(DriverAuthContext);
  const [order, setOrder] = useState(null);
  const [report, setReport] = useState(null);
  const [form, setForm] = useState({
    arrivedAt: '', completedAt: '', actualDurationMin: '',
    workPerformed: '', materials: [],
    outcome: 'resolved', followUpNotes: '',
    customerName: '', customerSignature: '', customerRating: 0,
    photos: [], notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [materialRow, setMaterialRow] = useState({ name: '', qty: '', unit: '' });

  async function load() {
    try {
      const res = await fetch(`${api}/mobile/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Falha ao carregar OT');
      const data = await res.json();
      setOrder(data);
      const r = data.assistanceReport;
      if (r) {
        setReport(r);
        setForm({
          arrivedAt: r.arrivedAt ? new Date(r.arrivedAt).toISOString().slice(0, 16) : '',
          completedAt: r.completedAt ? new Date(r.completedAt).toISOString().slice(0, 16) : '',
          actualDurationMin: r.actualDurationMin != null ? String(r.actualDurationMin) : '',
          workPerformed: r.workPerformed || '',
          materials: Array.isArray(r.materialsUsed) ? r.materialsUsed : [],
          outcome: r.outcome || 'resolved',
          followUpNotes: r.followUpNotes || '',
          customerName: r.customerName || '',
          customerSignature: r.customerSignature || '',
          customerRating: r.customerRating || 0,
          photos: Array.isArray(r.photos) ? r.photos : [],
          notes: r.notes || '',
        });
      }
    } catch (e) { setError(e.message); }
  }

  useEffect(() => { load(); }, [orderId]); // eslint-disable-line

  async function markArrived() {
    const now = new Date().toISOString().slice(0, 16);
    setForm(f => ({ ...f, arrivedAt: now }));
  }

  async function saveReport() {
    if (busy) return;
    setBusy(true); setSaved(false); setError('');
    try {
      const body = {
        arrivedAt: form.arrivedAt || null,
        completedAt: form.completedAt || (form.outcome === 'resolved' || form.outcome === 'cancelled' ? new Date().toISOString() : null),
        actualDurationMin: form.actualDurationMin !== '' ? Number(form.actualDurationMin) : null,
        workPerformed: form.workPerformed || null,
        materialsUsed: form.materials || [],
        outcome: form.outcome,
        followUpNotes: form.followUpNotes || null,
        customerName: form.customerName || null,
        customerSignature: form.customerSignature || null,
        customerRating: form.customerRating || null,
        photos: form.photos || [],
        notes: form.notes || null,
      };
      const res = await fetch(`${api}/mobile/orders/${orderId}/report`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Falha ao guardar');
      const saved = await res.json();
      setReport(saved);
      setSaved(true);
      // Refresh underlying OT
      load();
      // If the backend auto-completed the route (this was the last OT),
      // bounce back to the routes list after a short pause so the driver
      // sees the success state. Otherwise stay on the OT detail.
      if (saved?.routeAutoCompleted) {
        setTimeout(() => {
          window.location.hash = '#/driver/routes';
        }, 1500);
      } else {
        setTimeout(() => setSaved(false), 2400);
      }
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  function addMaterial() {
    const name = materialRow.name.trim();
    if (!name) return;
    setForm(f => ({
      ...f,
      materials: [...(f.materials || []), {
        name,
        qty: materialRow.qty ? Number(materialRow.qty) : null,
        unit: materialRow.unit || null,
      }],
    }));
    setMaterialRow({ name: '', qty: '', unit: '' });
  }
  function removeMaterial(idx) {
    setForm(f => ({ ...f, materials: (f.materials || []).filter((_, i) => i !== idx) }));
  }

  function handlePhotoPick(e) {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setForm(f => ({ ...f, photos: [...(f.photos || []), { url: reader.result, caption: '' }] }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  }

  if (!order && !error) {
    return <Box style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>;
  }
  if (!order) {
    return (
      <Box style={{ padding: 20, textAlign: 'center' }}>
        <Typography>OT não encontrada.</Typography>
        <Button onClick={() => window.history.back()}>Voltar</Button>
      </Box>
    );
  }

  const isAssistance = order.serviceType === 'assistance';

  return (
    // Full-height flex column with scrollable middle — the sticky footer was
    // eating the bottom of the form. Now the header stays fixed up top, the
    // content scrolls freely in the middle, and the footer is pinned below.
    <Box style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f1f5f9', overflow: 'hidden' }}>
      {/* Header */}
      <Box style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff',
        padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <IconButton onClick={() => window.history.back()} style={{ color: '#fff', padding: 4 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M15.41 16.59 10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/></svg>
        </IconButton>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Typography style={{ fontSize: '1rem', fontWeight: 800 }}>
            {order.orderNumber || `OT-${order.id}`}
          </Typography>
          <Typography style={{ fontSize: '0.7rem', opacity: 0.9 }}>
            {isAssistance ? '🔧 Assistência técnica' : '📦 Entrega de mercadoria'}
          </Typography>
        </Box>
      </Box>

      {/* Scrollable content area — takes remaining space between sticky header and footer */}
      <Box style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: 12 }}><Box>
        <Box style={{ background: '#fff', borderRadius: 12, padding: 12, marginBottom: 10 }}>
          {order.slaHours != null && (
            <Box style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, background: '#fef3c7', color: '#92400e', fontSize: '0.68rem', fontWeight: 700, marginBottom: 8 }}>
              ⏱ Prazo contratual: {order.slaHours}h
            </Box>
          )}
          {isAssistance ? (
            <>
              <Typography style={{ fontSize: '0.64rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>🔧 Local do serviço</Typography>
              <Typography style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>{order.deliveryName || '—'}</Typography>
              <Typography style={{ fontSize: '0.74rem', color: '#334155' }}>{order.deliveryAddress || ''}</Typography>
              {order.deliveryPhone && <Typography style={{ fontSize: '0.74rem', color: '#334155' }}>📞 {order.deliveryPhone}</Typography>}
              {order.deliveryInstructions && (
                <Box style={{ marginTop: 6, padding: 6, background: '#fffbeb', borderLeft: '3px solid #f59e0b', fontSize: '0.74rem', color: '#92400e' }}>
                  ℹ {order.deliveryInstructions}
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography style={{ fontSize: '0.64rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>📍 Entrega</Typography>
              <Typography style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>{order.deliveryName || '—'}</Typography>
              <Typography style={{ fontSize: '0.74rem', color: '#334155' }}>{order.deliveryAddress || ''}</Typography>
            </>
          )}
          {order.unloadingTime && (
            <Typography style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 6 }}>
              Tempo estimado do serviço: <b>{order.unloadingTime} min</b>
            </Typography>
          )}
        </Box>

        {/* Report form — shown for all order types. The heading and labels
            adapt to the service type so the driver sees the right terminology. */}
        {(() => {
          const reportHeading = isAssistance
            ? '📝 Relatório da assistência'
            : order.serviceType === 'fuel'
              ? '📝 Relatório de entrega (combustível)'
              : '📝 Relatório de entrega';
          return (
          <>
            <Typography style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a', marginTop: 14, marginBottom: 6 }}>
              {reportHeading}
            </Typography>

            <Box style={{ background: '#fff', borderRadius: 12, padding: 12 }}>
              {/* Times */}
              <Box style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b', marginBottom: 2 }}>Cheguei ao local</Typography>
                  <Box style={{ display: 'flex', gap: 4 }}>
                    <TextField type="datetime-local" value={form.arrivedAt}
                      onChange={e => setForm(f => ({ ...f, arrivedAt: e.target.value }))}
                      variant="outlined" size="small" fullWidth
                      inputProps={{ style: { padding: '6px 8px', fontSize: '0.74rem' } }} />
                    <Button size="small" onClick={markArrived}
                      style={{ fontSize: '0.64rem', fontWeight: 700, minWidth: 48, color: '#2563eb', textTransform: 'none' }}>
                      Agora
                    </Button>
                  </Box>
                </Box>
                <Box style={{ width: 110 }}>
                  <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b', marginBottom: 2 }}>Duração (min)</Typography>
                  <TextField type="number" value={form.actualDurationMin}
                    onChange={e => setForm(f => ({ ...f, actualDurationMin: e.target.value }))}
                    variant="outlined" size="small" fullWidth
                    inputProps={{ style: { padding: '6px 8px', fontSize: '0.74rem' }, min: 0 }} />
                </Box>
              </Box>

              {/* Work performed */}
              <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b', marginBottom: 2 }}>Trabalho realizado</Typography>
              <TextField multiline rows={3} fullWidth variant="outlined" size="small"
                placeholder="Descreve o que fizeste: diagnóstico, reparação, peças substituídas..."
                value={form.workPerformed}
                onChange={e => setForm(f => ({ ...f, workPerformed: e.target.value }))}
                inputProps={{ style: { fontSize: '0.78rem' } }}
                style={{ marginBottom: 12 }} />

              {/* Materials */}
              <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>🔩 Peças / Materiais usados</Typography>
              {(form.materials || []).map((m, i) => (
                <Box key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: '#f8fafc', borderRadius: 6, marginBottom: 4 }}>
                  <Typography style={{ fontSize: '0.76rem', fontWeight: 600, flex: 1 }}>{m.name}</Typography>
                  {m.qty != null && <Typography style={{ fontSize: '0.74rem', color: '#64748b' }}>× {m.qty} {m.unit || ''}</Typography>}
                  <IconButton size="small" onClick={() => removeMaterial(i)} style={{ padding: 2 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#dc2626"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                  </IconButton>
                </Box>
              ))}
              <Box style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                <TextField placeholder="Peça/material" value={materialRow.name}
                  onChange={e => setMaterialRow(r => ({ ...r, name: e.target.value }))}
                  variant="outlined" size="small" style={{ flex: 2 }}
                  inputProps={{ style: { padding: '5px 8px', fontSize: '0.74rem' } }} />
                <TextField placeholder="Qtd" value={materialRow.qty} type="number"
                  onChange={e => setMaterialRow(r => ({ ...r, qty: e.target.value }))}
                  variant="outlined" size="small" style={{ width: 70 }}
                  inputProps={{ style: { padding: '5px 8px', fontSize: '0.74rem', textAlign: 'right' }, min: 0 }} />
                <TextField placeholder="Un" value={materialRow.unit}
                  onChange={e => setMaterialRow(r => ({ ...r, unit: e.target.value }))}
                  variant="outlined" size="small" style={{ width: 60 }}
                  inputProps={{ style: { padding: '5px 8px', fontSize: '0.74rem' } }} />
                <Button size="small" onClick={addMaterial} variant="contained"
                  style={{ background: '#2563eb', color: '#fff', textTransform: 'none', fontSize: '0.7rem', minWidth: 36, padding: '4px 8px' }}>+</Button>
              </Box>

              {/* Outcome */}
              <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>Resultado</Typography>
              <Box style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                {OUTCOMES.map(o => {
                  const active = form.outcome === o.key;
                  return (
                    <Box key={o.key} onClick={() => setForm(f => ({ ...f, outcome: o.key }))}
                      style={{
                        padding: '5px 10px', borderRadius: 14, cursor: 'pointer',
                        fontSize: '0.7rem', fontWeight: active ? 700 : 600,
                        background: active ? o.color : '#fff',
                        color: active ? '#fff' : o.color,
                        border: `1.5px solid ${active ? o.color : `${o.color}40`}`,
                      }}>
                      {o.label}
                    </Box>
                  );
                })}
              </Box>

              {/* Follow-up */}
              {(form.outcome === 'followup_needed' || form.outcome === 'partial') && (
                <>
                  <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b', marginBottom: 2 }}>Notas para nova visita</Typography>
                  <TextField multiline rows={2} fullWidth variant="outlined" size="small"
                    placeholder="O que ficou por fazer, peças a encomendar, etc."
                    value={form.followUpNotes}
                    onChange={e => setForm(f => ({ ...f, followUpNotes: e.target.value }))}
                    inputProps={{ style: { fontSize: '0.78rem' } }}
                    style={{ marginBottom: 12 }} />
                </>
              )}

              {/* Photos */}
              <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>📷 Fotografias ({(form.photos || []).length})</Typography>
              <Box style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                {(form.photos || []).map((p, i) => (
                  <Box key={i} style={{ position: 'relative' }}>
                    <img src={p.url} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} />
                    <IconButton size="small"
                      onClick={() => setForm(f => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }))}
                      style={{ position: 'absolute', top: -6, right: -6, background: '#dc2626', padding: 2 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                    </IconButton>
                  </Box>
                ))}
              </Box>
              <Button size="small" component="label"
                style={{ fontSize: '0.72rem', textTransform: 'none', color: '#2563eb', border: '1px dashed #93c5fd', padding: '6px 10px', marginBottom: 12 }}>
                + Adicionar foto
                <input hidden type="file" accept="image/*" multiple capture="environment" onChange={handlePhotoPick} />
              </Button>

              {/* Customer ack */}
              <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b', marginTop: 4, marginBottom: 2 }}>Nome do cliente (quem recebeu)</Typography>
              <TextField fullWidth variant="outlined" size="small" placeholder="Nome de quem recebeu o serviço"
                value={form.customerName}
                onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}
                inputProps={{ style: { padding: '6px 8px', fontSize: '0.78rem' } }}
                style={{ marginBottom: 8 }} />

              <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>✍ Assinatura do cliente</Typography>
              <SignaturePad value={form.customerSignature} onChange={(dataUrl) => setForm(f => ({ ...f, customerSignature: dataUrl }))} />

              <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b', marginTop: 10, marginBottom: 2 }}>Notas internas</Typography>
              <TextField multiline rows={2} fullWidth variant="outlined" size="small"
                placeholder="Observações para o gestor"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                inputProps={{ style: { fontSize: '0.78rem' } }} />
            </Box>

            {error && (
              <Box style={{ background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: '0.78rem', marginTop: 10 }}>
                {error}
              </Box>
            )}
            {saved && (
              <Box style={{ background: '#dcfce7', color: '#166534', padding: 10, borderRadius: 8, fontSize: '0.78rem', marginTop: 10, fontWeight: 700 }}>
                ✓ Relatório guardado
              </Box>
            )}
          </>
          );
        })()}
      </Box>
      </Box>

      {/* Non-sticky save footer — positioned naturally at the bottom of the flex column.
          The button label is action-oriented: "Concluir serviço" makes it clear
          this both saves the report AND marks the OT as delivered (when outcome=resolved). */}
      <Box style={{
          flexShrink: 0,
          padding: '10px 14px',
          background: '#fff', borderTop: '1px solid #e2e8f0',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          {!report && (
            <Typography style={{ fontSize: '0.7rem', color: '#64748b', textAlign: 'center' }}>
              {isAssistance
                ? 'Preenche o relatório e toca para concluir o serviço'
                : 'Confirma a entrega para concluir esta OT'}
            </Typography>
          )}
          <Button
            fullWidth variant="contained" disabled={busy}
            onClick={saveReport}
            style={{
              padding: '14px', borderRadius: 10, textTransform: 'none',
              fontSize: '0.95rem', fontWeight: 800,
              background: busy ? '#cbd5e1' : 'linear-gradient(135deg, #16a34a, #15803d)', color: '#fff',
              boxShadow: '0 2px 8px rgba(22,163,74,0.3)',
            }}>
            {busy
              ? 'A guardar...'
              : report
                ? '💾 Atualizar relatório'
                : (isAssistance ? '✓ Concluir serviço' : '✓ Concluir entrega')}
          </Button>
        </Box>
    </Box>
  );
}
